import React from "react";
import { Button, Card, CardBody, CardHeader, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import useVehicle from "../../../../hooks/master-data/useVehicle";
import useListCompany from "../../../../hooks/useListCompany";
import { PlusIcon } from "@heroicons/react/24/outline";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import ControlledInput from "../../../../components/elements/ControlledInput";
import ModalHapusKendaraan from "../../../../components/fragments/admin/vehicle/ModalHapusKendaraan";
import ControlledReactSelect from "../../../../components/elements/ControlledReactSelect";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import useSWR from "swr";
import { getFetcher } from "../../../../services/api";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import { hasValidRole } from "../../../../services/helpers";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";

export default function IndexPage() {
  const {
    columns,
    isEdit,
    onCloseForm,
    onSubmitForm,
    badRequest: { formErrorMessage },
    modalForm: { onOpenModalForm, isOpenModalForm, onOpenChangeModalForm },
    modalHapus: { isOpenModalHapus, onOpenChangeModalHapus, onTutupModalHapus, kendaraanDihapus, onBerhasilHapus },
    hookForm: {
      handleSubmit,
      control,
      formState: { isSubmitting },
    },
  } = useVehicle();

  const { roles } = useAuth();

  // Cek apakah user adalah Pengelola
  const isPengelola = hasValidRole(roles, [RolesAccess.PENGELOLA]);

  // Ambil data perusahaan user
  const { dataCompany, isLoadingCompany } = useListCompany();

  // Perusahaan yang dipilih.
  //
  // Memakai hook bersama, bukan useState(null): untuk pengguna non-super-admin
  // nilainya otomatis terisi perusahaan miliknya sendiri begitu data AuthContext
  // tersedia. Dengan useState(null), filter selalu mulai kosong sehingga
  // Pengelola - satu-satunya peran yang melihat filter ini - harus memilih
  // perusahaannya sendiri dulu sebelum daftar kendaraan muncul.
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();

  const { data, isLoading, mutate } = useSWR(isPengelola ? (selectedCompany ? `/api/vehicles/search?companyId=${selectedCompany.value}&returnAll=true` : null) : "/api/vehicles/search?returnAll=true", getFetcher, { fallbackData: { vehicles: [], total: 0 } });
  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Daftar Kendaraan</p>
        </CardHeader>
        <Divider />
        <CardBody>
          <div className="mb-5 flex justify-between items-center">
            {!isPengelola && (
              <Button onPress={onOpenModalForm} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
                Tambah
              </Button>
            )}
            {isPengelola && (
              <div className="w-1/4">
                <FilterReactSelect
                  defaultValue={selectedCompany}
                  options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                  setValue={setSelectedCompany}
                  placeholder="Pilih Perusahaan"
                  isLoading={isLoadingCompany}
                />
              </div>
            )}
          </div>
          <div className="w-full h-[500px]">
            <ClientDataGrid rows={data?.vehicles} rowCount={data?.total || 0} isLoading={isLoading} columns={columns} />
          </div>
        </CardBody>
      </Card>

      <ModalHapusKendaraan
        isOpen={isOpenModalHapus}
        onOpenChange={onOpenChangeModalHapus}
        onClose={onTutupModalHapus}
        kendaraan={kendaraanDihapus}
        onSuccess={() => {
          // Dua daftar perlu disegarkan: milik hook dan milik halaman ini, yang
          // memakai kunci SWR berbeda saat pengguna adalah Pengelola.
          onBerhasilHapus();
          mutate();
        }}
      />
      <Modal size="2xl" scrollBehavior="inside" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{isEdit ? "Ubah" : "Tambah"} Kendaraan</ModalHeader>
              <ModalBody>
                {formErrorMessage && (
                  <ul className="text-xs text-white bg-danger p-3">
                    {Object.entries(formErrorMessage).map(([key, value]) => (
                      <li key={key}>{value}</li>
                    ))}
                  </ul>
                )}
                <form onSubmit={handleSubmit(onSubmitForm)}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ControlledReactSelect
                      control={control}
                      label="Perusahaan"
                      isLoading={isLoadingCompany}
                      name="companyId"
                      menuPosition="absolute"
                      options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                      isDisabled={isEdit}
                    />
                    {/* Saat mengubah, hanya nomor polisi yang boleh disunting. Identitas
                        fisik kendaraan - merek, tahun, rangka, mesin, kepemilikan - dan
                        perusahaan pemiliknya dikunci, karena kendaraan yang sama sudah
                        terpakai di rekomendasi dan pelaporan pengangkutan. */}
                    <ControlledInput control={control} label="No. Polisi" name="noPolisi" isRequired />
                    <ControlledInput control={control} label="Merek Kendaraan" name="modelKendaraan" isRequired isDisabled={isEdit} />
                    <ControlledInput control={control} label="Tahun Pembuatan" name="tahunPembuatan" isRequired type="number" isDisabled={isEdit} />
                    <ControlledInput control={control} label="Nomor Rangka" name="nomorRangka" isRequired isDisabled={isEdit} />
                    <ControlledInput control={control} label="Nomor Mesin" name="nomorMesin" isRequired isDisabled={isEdit} />
                    <ControlledInput control={control} label="Kepemilikan" name="kepemilikan" isRequired isDisabled={isEdit} />
                  </div>

                  <div className="flex items-center gap-1 mt-5">
                    <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                      {isEdit ? "Simpan" : "Tambah"}
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
